import prices from 'js/lib/prices';
import requestCountry from 'js/lib/requestCountry';
import user from 'js/lib/user';

import paymentsBackendEpicClient from 'bundles/epic/clients/payments-backend';
import CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import { isS12nDerivativesToOwnableProductsEnabled } from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';
import ProductTypeObject from 'bundles/payments-common/common/ProductType';
import type { CartCreateOptions } from 'bundles/payments-common/common/types';
import createCartData from 'bundles/payments-common/data/createCart';
import { createCart as createCourseraPlusSubscriptionCart } from 'bundles/subscriptions-common/lib/courseraPlusSubscriptionsV1Client';
import createCredentialTrackSubscriptionCart from 'bundles/subscriptions-common/lib/credentialTrackSubscriptionsV1Client';
import { createCart as createSubscriptionCart } from 'bundles/subscriptions-common/lib/subscriptionsV1Client';

/**
 * @typedef ProductItem
 * @type Object
 * @property {string} productType - eg. ['VerifiedCertificate', 'Specialization', ...]
 * @property {string} productItemId - Id of product.
 * @property {string} productAction - Action ['Buy', 'Refund'] Most usages are 'Buy'
 * @property {object} cartItemIdToRefund - TODO: Ask Gringott's team.
 */

/**
 * POST request to create a cart. If you want to create a cart with auxiliaryInfo,
 * then pass auxiliaryInfo using bundles/payments-common/models/cart/auxiliaryInfo
 *
 * @param {string} options.paymentProcessorId - ['braintree', ...]
 * @param {string} options.userId - UserId of user creating the cart.
 * @param {string} [options.currencyCode] - 3 character currency code. eg 'USD'
 * @param {Array.<ProductItem>} options.productItems - List of productItems in this cart.
 * @returns {Promise.<CartsV2>} Promise of NaptimeJS CartsV2 model
 */
export default (cartCreateOptions: CartCreateOptions): Promise<CartsV2> => {
  const requestCountryCode = requestCountry.get();
  const isS12nSubscriptionCart = cartCreateOptions.productType === ProductTypeObject.SPECIALIZATION_SUBSCRIPTION;
  const isNonSubscriptionCart =
    ![
      ProductTypeObject.COURSERA_PLUS_SUBSCRIPTION,
      ProductTypeObject.SPECIALIZATION_SUBSCRIPTION,
      ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION,
      ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION_V2,
    ].includes(cartCreateOptions.productType || '') &&
    ![
      ProductTypeObject.COURSERA_PLUS_SUBSCRIPTION,
      ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION,
      ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION_V2,
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    ].includes(cartCreateOptions.productItems[0].productType || '');

  let currencyCode: string;
  if (requestCountryCode === 'BR' && isNonSubscriptionCart && paymentsBackendEpicClient.preview('enablePIX')) {
    // In PIX experiment, we want to use BRL for non-subscription carts
    currencyCode = 'BRL';
  } else if (requestCountryCode === 'PH' && paymentsBackendEpicClient.preview('enableLocalCurrencyPhp')) {
    // In DLocal poc, we want to use PHP when enableLocalCurrencyPhp is enabled
    currencyCode = 'PHP';
  } else if (requestCountryCode === 'PL' && isS12nSubscriptionCart && !isS12nDerivativesToOwnableProductsEnabled()) {
    // In the control variant of S12n Derivatives ownable Product migration, the currency on XDP is USD
    // instead of euro as in the enabled variant. We want the cart in control to be created in USD as well to keep consistent.
    // When Derivatives ownable Product migration is complete and being rolled out, we'll clean up this if block so everything is euro.
    // This applies only to s12n subscription carts that uses s12n derivatives or ownable products for price copy.
    currencyCode = 'USD';
  } else {
    currencyCode = prices.getCurrencyFromCountry(requestCountryCode);
  }
  const options = {
    userId: user.get().id,
    currencyCode,
    countryIsoCode: requestCountryCode,
    auxiliaryCartInfo: cartCreateOptions.auxiliaryCartInfo ? cartCreateOptions.auxiliaryCartInfo : [],
    ...cartCreateOptions,
  };

  if (
    options.productType === ProductTypeObject.COURSERA_PLUS_SUBSCRIPTION ||
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    options.productItems[0].productType === ProductTypeObject.COURSERA_PLUS_SUBSCRIPTION
  ) {
    return Promise.resolve(createCourseraPlusSubscriptionCart(options).then((response) => new CartsV2(response)));
  } else if (options.productType === ProductTypeObject.SPECIALIZATION_SUBSCRIPTION) {
    return Promise.resolve(createSubscriptionCart(options).then((response) => new CartsV2(response)));
  } else if (
    options.productType === ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION ||
    options.productType === ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION_V2 ||
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    options.productItems[0].productType === ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION ||
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    options.productItems[0].productType === ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION_V2
  ) {
    return Promise.resolve(createCredentialTrackSubscriptionCart(options).then((response) => new CartsV2(response)));
  } else {
    return Promise.resolve(createCartData(options).then((response: $TSFixMe) => new CartsV2(response.elements[0])));
  }
};
